import React, {
  useMemo,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import mapboxgl, {
  FillLayerSpecification,
  GeolocateControl,
  LineLayerSpecification,
  SymbolLayerSpecification
} from 'mapbox-gl';
import MapboxMap, { GeoJSONLayer } from 'components/MapboxMap';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { featureCollection } from '@turf/helpers';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import './index.css';
import {
  USA_CENTER_COORDINATES,
  MAX_ZOOM,
  MIN_ZOOM
} from 'screens/Property/helpers/constants';
import coordinatesGeocoder from 'screens/Property/PropertiesLanding/PropertyLandingMap/PropertyMapWrapper/PropertyMap/utilities/coordinatesGeocoder';
import getFieldLabelPoints from './labelPoints';

const AccessToken = "pk.eyJ1Ijoic3RyaWRlcmVuZ2luZWVyaW5nIiwiYSI6ImNsdWs5ZG1vNzA2azYyam1scHljMjNyc3kifQ.YBj-8NtAHftqXroxqmbxXw" as string;

const MAP_STYLE = 'mapbox://styles/mapbox/satellite-streets-v11';
const EMPTY_FC = featureCollection([]);

const BACKGROUND_FILL_PAINT: FillLayerSpecification['paint'] = {
  'fill-color': '#ffffff',
  'fill-opacity': 0.4,
  'fill-antialias': true
};
const BACKGROUND_LINE_PAINT: LineLayerSpecification['paint'] = {
  'line-color': '#ffffff',
  'line-width': 2.5
};
const SELECTED_FILL_PAINT: FillLayerSpecification['paint'] = {
  'fill-color': '#31B4F2',
  'fill-opacity': 0.4,
  'fill-antialias': true
};
const SELECTED_LINE_PAINT: LineLayerSpecification['paint'] = {
  'line-color': '#31B4F2',
  'line-width': 2.5
};
const HOVER_FILL_PAINT: FillLayerSpecification['paint'] = {
  'fill-color': '#31B4F2',
  'fill-opacity': 0.75, // #31B4F2BF (BF hex = 191/255 ≈ 0.75)
  'fill-antialias': true
};
const HOVER_LINE_PAINT: LineLayerSpecification['paint'] = {
  'line-color': '#0092E4', // Blue/blue-50
  'line-width': 2.5
};
const DIMMED_FILL_PAINT: FillLayerSpecification['paint'] = {
  'fill-color': '#868CA2',
  'fill-opacity': 0.5,
  'fill-antialias': true
};
const DIMMED_LINE_PAINT: LineLayerSpecification['paint'] = {
  'line-color': '#363948',
  'line-width': 3.5
};

const FIT_BOUNDS_OPTIONS = { padding: 80, animate: false };
const FLY_TO_OPTIONS = { speed: 100 };

const LABEL_SYMBOL_LAYOUT: SymbolLayerSpecification['layout'] = {
  'text-field': ['get', 'name'],
  // Matches the property-landing map's field labels.
  'text-size': 20,
  'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
  'text-anchor': 'center' as const,
  'text-allow-overlap': false,
  'text-max-width': 8
};
const LABEL_SYMBOL_PAINT: SymbolLayerSpecification['paint'] = {
  'text-color': '#FFFFFF'
};

// v2's own pill styling; only the font stack matches the landing map.
const ARCHIVED_BADGE_SYMBOL_LAYOUT: SymbolLayerSpecification['layout'] = {
  'text-field': 'ARCHIVED',
  'text-size': 11,
  'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
  'text-anchor': 'center' as const,
  'text-allow-overlap': true,
  'text-ignore-placement': true,
  'text-letter-spacing': 0.06,
  'text-offset': [0, 1.5] as [number, number]
};
const ARCHIVED_BADGE_SYMBOL_PAINT: SymbolLayerSpecification['paint'] = {
  'text-color': '#DFE2E7',
  'text-halo-color': '#363948',
  'text-halo-width': 8,
  'text-halo-blur': 6
};

const categorizeFeature = (
  f: any,
  searchActive: boolean,
  visibleFieldIds: Set<string> | null,
  fullySelectedFieldIds: Set<string>,
  selectedIds: Set<string>,
  czToFieldMap: Map<string, string>,
  buckets: { bg: any[]; dimmed: any[]; hlFields: any[]; hlCropzones: any[] }
) => {
  if (!f?.geometry?.coordinates) return;
  const { id, $landType } = f.properties ?? {};
  if ($landType === 'field') {
    if (f.properties?._cropFilterDisabled) {
      buckets.dimmed.push(f);
    } else if (searchActive && visibleFieldIds && !visibleFieldIds.has(id)) {
      buckets.dimmed.push(f);
    } else if (fullySelectedFieldIds.has(id)) {
      buckets.hlFields.push(f);
    } else {
      buckets.bg.push(f);
    }
  } else {
    const parentFieldId = czToFieldMap.get(id);
    if (
      (searchActive &&
        visibleFieldIds &&
        parentFieldId &&
        !visibleFieldIds.has(parentFieldId)) ||
      (parentFieldId && fullySelectedFieldIds.has(parentFieldId))
    )
      return;
    if (selectedIds.has(id)) buckets.hlCropzones.push(f);
  }
};

const ClearSearchIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
    className="shrink-0"
  >
    <path
      d="M8 1.33334C4.32 1.33334 1.33334 4.32 1.33334 8C1.33334 11.68 4.32 14.6667 8 14.6667C11.68 14.6667 14.6667 11.68 14.6667 8C14.6667 4.32 11.68 1.33334 8 1.33334ZM11.3333 10.3933L10.3933 11.3333L8 8.94L5.60667 11.3333L4.66667 10.3933L7.06 8L4.66667 5.60667L5.60667 4.66667L8 7.06L10.3933 4.66667L11.3333 5.60667L8.94 8L11.3333 10.3933Z"
      fill="#696F88"
    />
  </svg>
);

const ZoomInIcon = () => (
  <svg
    aria-hidden="true"
    width="10"
    height="10"
    viewBox="0 0 10 10"
    fill="none"
  >
    <path
      d="M9.66659 5.66659H5.66659V9.66659H4.33325V5.66659H0.333252V4.33325H4.33325V0.333252H5.66659V4.33325H9.66659V5.66659Z"
      fill="#696F88"
    />
  </svg>
);

const ZoomOutIcon = () => (
  <svg aria-hidden="true" width="10" height="2" viewBox="0 0 10 2" fill="none">
    <path
      d="M9.66659 1.66659H0.333252V0.333252H9.66659V1.66659Z"
      fill="#696F88"
    />
  </svg>
);

interface SelectionMapProps {
  geoJSONCollection: any;
  cropZonesChecked: string[];
  center?: number[];
  zoom?: number[];
  fitBoundsTarget?: [[number, number], [number, number]] | null;
  onZoomChange?: (zoom: number[]) => void;
  onCenterChange?: (coords: number[]) => void;
  onClickFeature?: (event: any) => void;
  searchActive?: boolean;
  visibleFieldIds?: Set<string> | null;
  onClearSearch?: () => void;
  isSearchLoading?: boolean;
}

const SelectionMap: React.FC<SelectionMapProps> = ({
  geoJSONCollection,
  cropZonesChecked,
  center,
  zoom,
  fitBoundsTarget,
  onZoomChange,
  onCenterChange,
  onClickFeature,
  searchActive = false,
  visibleFieldIds = null,
  onClearSearch,
  isSearchLoading = false
}) => {
  const mapInstanceRef = useRef<any>(null);
  const [hoveredFieldId, setHoveredFieldId] = useState<string | null>(null);
  const hoveredIdRef = useRef<string | null>(null);

  const selectedIds = useMemo(() => new Set(cropZonesChecked), [
    cropZonesChecked
  ]);

  const geocoder = useMemo(
    () =>
      new MapboxGeocoder({
        accessToken: AccessToken,
        collapsed: true,
        localGeocoder: coordinatesGeocoder,
        placeholder: 'ZIP, Address, Lat/Long',
        mapboxgl
      }),
    []
  );

  const geolocateControl = useMemo(
    () =>
      new GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true
      }),
    []
  );

  // Keep refs so map-level handlers (registered once on style load) always
  // read the latest prop values without needing to be re-registered.
  const onClickFeatureRef = useRef(onClickFeature);
  useEffect(() => {
    onClickFeatureRef.current = onClickFeature;
  }, [onClickFeature]);

  const searchActiveRef = useRef(searchActive);
  useEffect(() => {
    searchActiveRef.current = searchActive;
  }, [searchActive]);

  const visibleFieldIdsRef = useRef(visibleFieldIds);
  useEffect(() => {
    visibleFieldIdsRef.current = visibleFieldIds;
  }, [visibleFieldIds]);

  const onStyleLoad = useCallback(
    (map: any) => {
      mapInstanceRef.current = map;
      map.resize();
      map.addControl(geocoder, 'bottom-right');
      map.addControl(geolocateControl, 'bottom-right');
      geocoder.on('result', (e: any) => {
        const coords = e.result?.center;
        if (coords) onCenterChange?.(coords);
      });

      // Register a single map-level click handler. Using queryRenderedFeatures
      // is more reliable than GeoJSONLayer fillOnClick because that prop
      // creates a new function reference every render, tearing down and
      // re-registering the Mapbox listener continuously.
      map.on('click', (e: any) => {
        const handler = onClickFeatureRef.current;
        if (!handler) return;
        const allFeatures: any[] = map.queryRenderedFeatures(e.point) ?? [];
        // Deduplicate by properties.id — overlapping layers (e.g. hover + background)
        // can return the same feature twice, which would cause a double-toggle.
        // Also block clicks on non-matching (dimmed) fields when search is active.
        const seen = new Set<string>();
        const features = allFeatures.filter((f: any) => {
          const id = f?.properties?.id;
          if (!id || seen.has(id)) return false;
          seen.add(id);
          if (
            f?.properties?.$landType === 'field' &&
            f?.properties?._cropFilterDisabled
          ) {
            return false;
          }
          const fieldIds = visibleFieldIdsRef.current;
          if (
            searchActiveRef.current &&
            fieldIds &&
            f?.properties?.$landType === 'field' &&
            !fieldIds.has(id)
          ) {
            return false;
          }
          return true;
        });
        if (features.length > 0) {
          handler({ ...e, features });
        }
      });

      // Hover: highlight the field the cursor is over. Only update React state
      // when the hovered feature actually changes to keep re-renders minimal.
      map.on('mousemove', (e: any) => {
        const features: any[] = map.queryRenderedFeatures(e.point) ?? [];
        const fieldFeature = features.find((f: any) => {
          if (
            f?.properties?.$landType !== 'field' ||
            f?.properties?._cropFilterDisabled
          )
            return false;
          const fieldIds = visibleFieldIdsRef.current;
          if (
            searchActiveRef.current &&
            fieldIds &&
            !fieldIds.has(f.properties.id)
          )
            return false;
          return true;
        });
        const newId: string | null = fieldFeature?.properties?.id ?? null;
        if (newId !== hoveredIdRef.current) {
          hoveredIdRef.current = newId;
          setHoveredFieldId(newId);
          const canvas = mapInstanceRef.current?.getCanvas();
          if (canvas) canvas.style.cursor = newId ? 'pointer' : '';
        }
      });

      map.on('mouseout', () => {
        if (hoveredIdRef.current !== null) {
          hoveredIdRef.current = null;
          setHoveredFieldId(null);
          const canvas = mapInstanceRef.current?.getCanvas();
          if (canvas) canvas.style.cursor = '';
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [geocoder, geolocateControl]
  );

  // When a new fit target arrives, resize the map first so fitBounds works
  // correctly even if the drawer container was previously hidden (display:none).
  // After fitting, sync the resulting center back to the parent so that
  // MapboxMap's subsequent flyTo (triggered by the zoom-state update from
  // onZoomEnd) uses the fitted center rather than the old centroid or manualCoords.
  useEffect(() => {
    if (!fitBoundsTarget || !mapInstanceRef.current) return;
    mapInstanceRef.current.resize();
    mapInstanceRef.current.fitBounds(fitBoundsTarget, FIT_BOUNDS_OPTIONS);
    onCenterChange?.(mapInstanceRef.current.getCenter().toArray());
  }, [fitBoundsTarget]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build fieldId → Set<cropzoneId> using $parentFieldId on cropzone features.
  // This only counts crop zones actually present in the GeoJSON (current season),
  // whereas the serialised `cropzones` on field features can include all seasons.
  const fieldCropzoneMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    (geoJSONCollection?.features ?? []).forEach((f: any) => {
      if (f?.properties?.$landType !== 'cropzone') return;
      const { id, $parentFieldId } = f.properties ?? {};
      if (!id || !$parentFieldId) return;
      if (!map.has($parentFieldId)) map.set($parentFieldId, new Set());
      map.get($parentFieldId)!.add(id);
    });
    return map;
  }, [geoJSONCollection]);

  const fullySelectedFieldIds = useMemo(() => {
    const fully = new Set<string>();
    fieldCropzoneMap.forEach((czIds, fieldId) => {
      let selectedCount = 0;
      czIds.forEach(id => {
        if (selectedIds.has(id)) selectedCount += 1;
      });
      if (selectedCount === czIds.size && selectedCount > 0) fully.add(fieldId);
    });
    return fully;
  }, [fieldCropzoneMap, selectedIds]);

  const czToFieldMap = useMemo(() => {
    const map = new Map<string, string>();
    fieldCropzoneMap.forEach((czIds, fieldId) =>
      czIds.forEach(czId => map.set(czId, fieldId))
    );
    return map;
  }, [fieldCropzoneMap]);

  // ONE anchor point per field: a symbol layer over polygon geometry is
  // re-placed per vector tile / zoom level (duplicate names, jumping badge).
  // Computed once per data load; search only filters the precomputed points.
  const allFieldLabelPoints = useMemo(
    () =>
      getFieldLabelPoints(
        (geoJSONCollection?.features ?? []).filter(
          (f: any) =>
            f?.properties?.$landType === 'field' &&
            !f?.properties?._cropFilterDisabled &&
            !!f?.geometry?.coordinates?.length
        )
      ),
    [geoJSONCollection]
  );

  const fieldLabelFeatures = useMemo(() => {
    if (!searchActive || !visibleFieldIds) return allFieldLabelPoints;
    return featureCollection(
      allFieldLabelPoints.features.filter((f: any) =>
        visibleFieldIds.has(f.properties.id)
      )
    );
  }, [allFieldLabelPoints, searchActive, visibleFieldIds]);

  const archivedLabelFeatures = useMemo(
    () =>
      featureCollection(
        fieldLabelFeatures.features.filter(
          (f: any) => !!f?.properties?.archived
        )
      ),
    [fieldLabelFeatures]
  );

  const fieldFeatureMap = useMemo(() => {
    const map = new Map<string, any>();
    (geoJSONCollection?.features ?? []).forEach((f: any) => {
      if (
        f?.properties?.$landType === 'field' &&
        f?.properties?.id &&
        !f?.properties?._cropFilterDisabled
      ) {
        map.set(f.properties.id, f);
      }
    });
    return map;
  }, [geoJSONCollection]);

  const hoverFeatures = useMemo(() => {
    if (!hoveredFieldId) return EMPTY_FC;
    const feature = fieldFeatureMap.get(hoveredFieldId);
    return feature ? featureCollection([feature]) : EMPTY_FC;
  }, [hoveredFieldId, fieldFeatureMap]);

  // Rendering rules per AC:
  // • Nothing selected      → field in white bg, no cropzone geometry shown
  // • All CZs selected      → field in blue highlight, cropzone geometries hidden
  // • Some CZs selected     → field in white bg, only selected CZs in blue highlight
  // • Search active         → non-matching fields rendered dimmed (gray), matching fields as normal
  const {
    backgroundFeatures,
    dimmedFeatures,
    highlightedFieldFeatures,
    highlightedCropzoneFeatures
  } = useMemo(() => {
    const buckets = {
      bg: [] as any[],
      dimmed: [] as any[],
      hlFields: [] as any[],
      hlCropzones: [] as any[]
    };
    (geoJSONCollection?.features ?? []).forEach((f: any) =>
      categorizeFeature(
        f,
        searchActive,
        visibleFieldIds,
        fullySelectedFieldIds,
        selectedIds,
        czToFieldMap,
        buckets
      )
    );
    return {
      backgroundFeatures: featureCollection(buckets.bg),
      dimmedFeatures: featureCollection(buckets.dimmed),
      highlightedFieldFeatures: featureCollection(buckets.hlFields),
      highlightedCropzoneFeatures: featureCollection(buckets.hlCropzones)
    };
  }, [
    geoJSONCollection,
    fullySelectedFieldIds,
    czToFieldMap,
    selectedIds,
    searchActive,
    visibleFieldIds
  ]);

  const mapCenter =
    (center as [number, number]) ??
    (USA_CENTER_COORDINATES as [number, number]);

  const handleZoomIn = () => {
    const currentZoom = (zoom ?? [4])[0];
    onZoomChange?.([Math.min(currentZoom + 1, MAX_ZOOM)]);
  };

  const handleZoomOut = () => {
    const currentZoom = (zoom ?? [4])[0];
    onZoomChange?.([Math.max(currentZoom - 1, MIN_ZOOM)]);
  };

  return (
    <div
      className="selection-map"
      style={{ position: 'relative', height: '100%', width: '100%' }}
    >
      <MapboxMap
        // eslint-disable-next-line react/style-prop-object
        style={MAP_STYLE}
        center={mapCenter}
        zoom={(zoom ?? [4]) as [number]}
        flyToOptions={FLY_TO_OPTIONS}
        fitBounds={fitBoundsTarget ?? undefined}
        fitBoundsOptions={fitBoundsTarget ? FIT_BOUNDS_OPTIONS : undefined}
        containerStyle={{ height: '100%', width: '100%' }}
        onStyleLoad={onStyleLoad}
        onZoomEnd={(map: any) => onZoomChange?.([map.getZoom()])}
        onDragEnd={(map: any) => onCenterChange?.(map.getCenter().toArray())}
      >
        {/* Non-matching fields (search active) rendered dimmed */}
        <GeoJSONLayer
          data={dimmedFeatures}
          fillPaint={DIMMED_FILL_PAINT}
          linePaint={DIMMED_LINE_PAINT}
        />

        {/* All non-excluded features as neutral background */}
        <GeoJSONLayer
          data={backgroundFeatures}
          fillPaint={BACKGROUND_FILL_PAINT}
          linePaint={BACKGROUND_LINE_PAINT}
        />

        {/* Hover highlight — sits above background, below selected */}
        <GeoJSONLayer
          data={hoverFeatures}
          fillPaint={HOVER_FILL_PAINT}
          linePaint={HOVER_LINE_PAINT}
        />

        {/* Highlight layers always mounted so label layer stays on top in Mapbox's stack */}
        <GeoJSONLayer
          data={highlightedFieldFeatures}
          fillPaint={SELECTED_FILL_PAINT}
          linePaint={SELECTED_LINE_PAINT}
        />
        <GeoJSONLayer
          data={highlightedCropzoneFeatures ?? EMPTY_FC}
          fillPaint={SELECTED_FILL_PAINT}
          linePaint={SELECTED_LINE_PAINT}
        />

        {/* Field name labels — always last so they render on top of all fill layers */}
        <GeoJSONLayer
          data={fieldLabelFeatures}
          symbolLayout={LABEL_SYMBOL_LAYOUT}
          symbolPaint={LABEL_SYMBOL_PAINT}
        />

        {/* ARCHIVED pill badge — rendered below the name for archived features */}
        <GeoJSONLayer
          data={archivedLabelFeatures}
          symbolLayout={ARCHIVED_BADGE_SYMBOL_LAYOUT}
          symbolPaint={ARCHIVED_BADGE_SYMBOL_PAINT}
        />
      </MapboxMap>

      {/* Search loading overlay — shown when filtering takes >2s */}
      {isSearchLoading && (
        <div
          data-testid="search-loading-overlay"
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(20, 21, 28, 0.25)',
            zIndex: 20,
            pointerEvents: 'all'
          }}
        />
      )}

      {/* Clear search overlay button — wrapped in a full-width flex container
          so `justify-center` centers the button reliably. Using left-1/2 +
          -translate-x-1/2 broke here because Tailwind v1 requires an explicit
          `transform` class for translate utilities, and even with it the
          centering wasn't consistent across browsers. */}
      {searchActive && onClearSearch && (
        <div className="absolute z-10 bottom-4 inset-x-0 flex justify-center pointer-events-none">
          <button
            type="button"
            onClick={onClearSearch}
            data-testid="clear-search-map-button"
            className="flex items-center gap-1.5 bg-white rounded shadow-md hover:bg-neutral-10 py-2 px-4 text-sm leading-5 font-normal pointer-events-auto"
            style={{ color: '#14151C', letterSpacing: '-0.105px' }}
          >
            <ClearSearchIcon />
            Clear search
          </button>
        </div>
      )}

      {/* Zoom buttons */}
      <div
        className="absolute flex flex-col z-10 right-0 bottom-0"
        data-testid="zoom-buttons"
      >
        <button
          type="button"
          aria-label="Zoom in"
          onClick={handleZoomIn}
          className="flex justify-center items-center bg-white shadow-lg w-6 h-6 m-5 mb-0 rounded-t"
        >
          <ZoomInIcon />
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={handleZoomOut}
          className="flex justify-center items-center bg-white shadow-lg w-6 h-6 m-5 mt-0 rounded-b"
        >
          <ZoomOutIcon />
        </button>
      </div>
    </div>
  );
};

export default SelectionMap;
